// Components
import ChartCross from "../../ChartCross";
import ColoredCircle from "../../ColoredCircle";
import Circle from "../../Circle";
import Loader from "../../Loader";
import Square from "../../Square";
import GraphRing from "../../GraphRing";
import Triangle from "../Triangle";

// Styles
import styles from "./index.module.css";

// Utilities
import { useContext, useMemo, useRef } from "react";
import { ValidatePDFContext } from "../../../../contexts/ValidatePDFContext";
import {
  calculateXPaddingC40Graph,
  calculateYPaddingC40Graph,
} from "../../../../utils/examReportsHelper";
import { eyeTypes } from "../../../../enums";

const RLFastC40Graph = ({
  pressedPoints,
  eye,
  gridType,
  containerSize = 624,
  tickNum = 3,
  borderWidth = 2,
  isLiveUpdate = null,
}) => {
  const { eyeLeft } = eyeTypes;
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const containerRef = useRef(null);
  const size = useMemo(
    () =>
      containerSize
        ? forBackendPdf
          ? containerSize * 0.6
          : containerSize * 0.7
        : 624,
    [containerSize, forBackendPdf]
  );
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / 624, [size]);
  const scale = 1;

  // Show loader while data is not yet processed
  if (pressedPoints.length === 0) {
    return <Loader containerHeight="624px" />;
  }

  const C40Rings = () => {
    const ringBaseDimension = size / tickNum;
    const baseMargin = size / 2;
    return (
      <>
        {Array.from({ length: tickNum }).map((_, i) => {
          const factor = tickNum - i;
          const computedMargin = baseMargin - (baseMargin / tickNum) * factor;
          const dimension = ringBaseDimension * factor;

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                marginTop: computedMargin,
                marginLeft: computedMargin,
              }}
              aria-label="c40-ring"
            >
              <GraphRing height={dimension} width={dimension} />
            </div>
          );
        })}
      </>
    );
  };

  return (
    <main
      ref={containerRef}
      className={styles.container}
      style={{
        "--cross-c40-height": `${size}px`,
        "--cross-c40-width": `${size}px`,
        "--cross-c40-border-width": `${borderWidth}px`,
        "--cross-c40-tick-size": `${tickSize}px`,
        "--half-height": `calc((${size}px - ${borderWidth}px) / 2)`,
        "--half-width": `calc((${size}px - ${borderWidth}px) / 2)`,
      }}
    >
      {/* blind spot */}
      {/* the 10-2 grid type exam shouldn't display it */}
      <div
        style={{
          position: "absolute",
          marginTop: forBackendPdf ? 152 * (scale + 0.05) : 191,
          // If it's the left eye, the triangle should be placed at the left side of the grid. If it's right, at the right side of the grid.
          marginLeft: forBackendPdf
            ? (190 + (eye === eyeLeft ? -113 : 95)) * scale
            : (190 + (eye === eyeLeft ? -100 : 145)) * scale,
        }}
      >
        <Triangle />
      </div>

      {C40Rings()}
      <ChartCross
        height={size}
        width={size}
        tickNum={tickNum}
        gridType={gridType}
      />

      {pressedPoints
        .filter((spot) => spot.seen === false && spot.completed)
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x, scaleFactor),
                paddingTop: calculateYPaddingC40Graph(spot.y, scaleFactor),
              }}
            >
              <Square size={scaleFactor * 8} />
            </div>
          );
        })}
      {/* mapping the not completed visual spots. Display an orange circle by default, but display a green circle on the "current visual spot" */}
      {pressedPoints
        .filter((spot) => !spot.completed && spot.decibelValue) // considering the non completed ones, with a valid decibelValue/intensity
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x, scaleFactor),
                paddingTop: calculateYPaddingC40Graph(spot.y, scaleFactor),
              }}
            >
              {!isLiveUpdate ? (
                "-"
              ) : (
                <ColoredCircle
                  isOrange={!spot.current}
                  size={scaleFactor * 8}
                />
              )}
            </div>
          );
        })}
      {/* mapping the completed visual spots (live update)*/}
      {/* It's used for report as a `seen` */}
      {pressedPoints
        .filter((spot) => spot.seen)
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x, scaleFactor),
                paddingTop: calculateYPaddingC40Graph(spot.y, scaleFactor),
              }}
            >
              <Circle size={scaleFactor * 10} />
            </div>
          );
        })}
    </main>
  );
};

export default RLFastC40Graph;
